import { merge } from 'lodash'
import { useEffect, useMemo } from 'react'
import { FieldErrors, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { isStaff } from '@/utils/type-guards'
import { useIsUS } from '@/hooks/useIsUS'
import { useIsSA } from '@/hooks/useIsSA'
import { useIsINSEnabled } from '@/hooks/useIsINSEnabled'

function usePatientProfileFormSchema(ssnLength: number) {
  const isUS = useIsUS()
  // SEHA (en-SA): no patient-agreement step, National ID/Iqama/Border Number
  // instead of the French SSN, mandatory birthday and emergency contact.
  const isSA = useIsSA()

  return useMemo(
    () =>
      z
        .object({
          identityConfirmed: z.boolean(),
          agreement: isSA ? z.string() : z.string().min(1),
          ignoreSecu: z.boolean().optional(),
          secu: isUS || isSA ? z.string().optional() : z.string(),
          insNIR: z.object({
            value: z
              .string()
              .nullable()
              .transform((value) => (value === '' ? null : value))
              .pipe(z.string().length(13).nullable()),
            key: z
              .string()
              .nullable()
              .transform((value) => (value === '' ? null : value))
              .pipe(z.string().length(2).nullable()),
          }),
          insOID: z.string().optional(),
          insHistory: z
            .array(
              z.object({
                value: z.string().optional(),
                key: z.string().optional(),
                startDate: z.string().optional(),
                endDate: z.string().optional(),
                oid: z.string().optional(),
              })
            )
            .optional(),
          name: z.string().min(2),
          birthName: z.string().optional(),
          surname: z.string().min(2),
          birthSurname: z.string().optional(),
          email: z.string().email().optional().or(z.literal('')),
          birthday: isSA
            ? z.date({ message: 'Date of birth is required' }).max(new Date())
            : z.date().max(new Date()).nullable().optional(),
          birthPlaceCode: z.string().optional(),
          gender: z.enum(['M', 'F', 'I', '']).optional(),
          nationality: z.string().optional(),
          phone: z.string(),
          caisse: z.string(),
          codeRegime: z.string(),
          codeGestion: z.string(),
          street: z.string(),
          city: z.string(),
          cp: z.string(),
          country: z.string(),
          weight: z
            .string()
            .optional()
            .transform((value) => (value === '' ? undefined : value))
            .pipe(z.string().min(2).max(3).optional()),
          height: z
            .string()
            .optional()
            .transform((value) => (value === '' ? undefined : value))
            .pipe(z.string().min(3).max(3).optional()),
          allergy: z.string(),
          vaccins: z.string(),
          notes: z.string(),
          background: z.string(),
          treatment: z.string(),
          parents: z
            .array(
              z.object({
                firstname: z.string().min(1),
                lastname: z.string().min(1),
                phone: z.string().optional(),
                email: z.string().email().optional().or(z.literal('')),
                city: z.string().optional(),
                cp: z.string().optional(),
              })
            )
            .min(isSA ? 1 : 0, 'At least one emergency contact is required'),
          privateDoctor: z
            .object({
              firstname: z.string().min(1),
              lastname: z.string().min(1),
              phone: z.string().optional(),
              email: z.string().email().optional().or(z.literal('')),
              city: z.string().optional(),
              cp: z.string().optional(),
            })
            .optional(),
          pluginId: z.string().optional(),
          insured: z.string().optional(),
          insuredRelationship: z.string().optional(),
          insuranceProvider: z.string().optional(),
          primaryInsurance: z.string().optional(),
          secondaryInsurance: z.string().optional(),
          primaryPolicy: z.string().optional(),
          secondaryPolicy: z.string().optional(),
          primaryPharmacy: z.string().optional(),
        })
        .superRefine((data, ctx) => {
          if (!isUS && !isSA && !data.ignoreSecu && data.secu !== undefined && data.secu.length !== ssnLength) {
            ctx.addIssue({
              code: 'custom',
              message: `Must be exactly ${ssnLength} characters`,
              path: ['secu'],
            })
          }
        }),
    [isUS, isSA, ssnLength]
  )
}

export type PatientProfileFormData = z.infer<ReturnType<typeof usePatientProfileFormSchema>>

export type UsePatientProfileFormProps = {
  initialValues?: Partial<PatientProfileFormData>
  errors?: FieldErrors<PatientProfileFormData>
}

export function usePatientProfileForm({ initialValues: initialValuesProp, errors = {} }: UsePatientProfileFormProps) {
  const isINSEnabled = useIsINSEnabled()
  const isSA = useIsSA()

  const authUser = useAppSelector(selectAuthUser)
  const ssnLength = authUser?.userInfo.language === 'it' ? 16 : 15

  const initialValues: PatientProfileFormData = useMemo(() => {
    return merge(
      {
        identityConfirmed: false,
        agreement: '',
        name: '',
        surname: '',
        ignoreSecu: false,
        secu: '',
        email: '',
        // SEHA only offers Male/Female, so do not preselect the Unknown gender there.
        gender: isSA ? '' : 'I',
        nationality: '',
        phone: '',
        birthday: null,
        caisse: '',
        codeRegime: '',
        codeGestion: '',
        street: '',
        city: '',
        cp: '',
        country: '',
        weight: '',
        height: '',
        allergy: '',
        vaccins: '',
        notes: '',
        background: '',
        treatment: '',
        parents: [],
        insNIR: {
          value: '',
          key: '',
        },
        insOID: '',
        insured: '',
        insuredRelationship: '',
        insuranceProvider: '',
        primaryInsurance: '',
        secondaryInsurance: '',
        primaryPolicy: '',
        secondaryPolicy: '',
        primaryPharmacy: '',
      },
      initialValuesProp
    )
  }, [initialValuesProp, isSA])

  const schema = usePatientProfileFormSchema(ssnLength)
  const rhForm = useForm<PatientProfileFormData>({ defaultValues: initialValues, errors, resolver: zodResolver(schema) })
  const { reset } = rhForm

  useEffect(() => {
    reset(initialValues, { keepErrors: true })
  }, [initialValues, reset])

  return {
    isINSEnabled: isINSEnabled && isStaff(authUser) && authUser.managePatientPreferences.insEnabled,
    rhForm,
    ssnLength,
  }
}
